/* ═══════════════════════════════════════════════════════════════
 * PrivacyThemeProvider.tsx
 *
 * Provider that:
 *
 *   1. Holds the active theme id in a module-level variable so
 *      it survives navigation between Privacy Suite windows
 *      WITHOUT touching `localStorage`. The choice resets on
 *      a hard reload — that is intentional: privacy-suite tabs
 *      should leave no on-disk trace.
 *   2. Writes the active theme's CSS custom properties to
 *      `document.documentElement.style` so every selector in the
 *      Privacy Suite can pick them up.
 *   3. Stamps `data-privacy-theme="<id>"` on `<html>` so theme-
 *      specific CSS overrides can target the active theme without
 *      polluting the global stylesheet.
 *   4. Exposes the current tokens + setter via React context.
 *
 * The provider is intentionally lightweight: there's only one
 * piece of state (the theme id), and the CSS plumbing runs as a
 * single effect. Anything fancier (animated transitions,
 * per-window scoping) can be layered on later.
 *
 * The provider also tolerates server-side rendering — it skips
 * the DOM write when `window` is undefined — so it's safe to mount
 * inside SSR-only wizard previews.
 *
 * The theme preference is in-memory ONLY: it is never read from or
 * written to `localStorage`, so a privacy-suite tab leaves no
 * on-disk trace and a hard reload always resets to the bundled
 * default. Phosphor is therefore a fun in-session toggle, not a
 * persisted preference.
 * ═══════════════════════════════════════════════════════════════ */

import {
	useCallback,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from 'react';
import {
	PrivacyThemeContext,
	type PrivacyThemeContextValue,
} from './PrivacyThemeContext';
import {
	buildPrivacyThemeVariables,
	DEFAULT_PRIVACY_THEME_ID,
	PRIVACY_THEME_REGISTRY,
	resolvePrivacyThemeId,
	type PrivacyThemeId,
} from './privacyThemeTokens';

/**
 * HTML attribute the provider stamps on `<html>` so theme-aware
 * CSS can scope its rules — e.g.
 *   `:root[data-privacy-theme="phosphor"] .xmr-panel { ... }`
 */
export const PRIVACY_THEME_ATTRIBUTE = 'data-privacy-theme';

export interface PrivacyThemeProviderProps {
	/**
	 * Override for the in-memory theme. When set, the provider
	 * uses this value verbatim and skips the module-level read.
	 * Used by tests + Storybook previews that want a deterministic
	 * starting state.
	 */
	readonly initialThemeId?: PrivacyThemeId;
	/**
	 * Children to render under the context. The provider does NOT
	 * render its own wrapper — Privacy Suite shells (`ZcashApp`,
	 * the wizards) get to control the surrounding DOM.
	 */
	readonly children: ReactNode;
}

/**
 * Process-lifetime cache for the user's chosen theme id. Shared
 * across every `PrivacyThemeProvider` instance (e.g. when one
 * wizard mounts inside another) so the selection survives
 * navigation between Privacy Suite windows without persisting
 * anything to disk.
 *
 * The cache resets when the page is reloaded — and is also
 * explicitly cleared by the program-manager Exit handler so
 * users who hit "Exit" see a clean default the next time they
 * boot the app.
 */
let _runtimeThemeId: PrivacyThemeId = DEFAULT_PRIVACY_THEME_ID;

/**
 * Test-only helper: reset the in-memory theme cache so each
 * test starts at the documented default. Exported under a
 * deliberately ugly name so production code can't accidentally
 * import it.
 */
export function __resetPrivacyThemeForTests(): void {
	_runtimeThemeId = DEFAULT_PRIVACY_THEME_ID;
}

/**
 * Reset the Privacy Suite theme to the bundled default. Called
 * by the program-manager "Exit" path so the next session starts
 * with no carryover from the previous one.
 */
export function clearPrivacyThemeRuntime(): void {
	_runtimeThemeId = DEFAULT_PRIVACY_THEME_ID;
}

/**
 * Apply the theme to the DOM: stamp the data attribute on
 * `<html>` and write the token variables onto the document root.
 *
 * Returns a cleanup that REMOVES the variables. The provider
 * doesn't actually unmount the cleanup itself (we keep the theme
 * applied for the lifetime of the page), but the function is
 * exported separately so tests can assert removal behaviour.
 */
function applyThemeToDocument(id: PrivacyThemeId): () => void {
	if (typeof document === 'undefined') return () => undefined;
	const root = document.documentElement;
	const tokens = PRIVACY_THEME_REGISTRY[id];
	root.setAttribute(PRIVACY_THEME_ATTRIBUTE, id);
	const variables = buildPrivacyThemeVariables(tokens);
	for (const [name, value] of Object.entries(variables)) {
		root.style.setProperty(name, value);
	}
	return () => {
		root.removeAttribute(PRIVACY_THEME_ATTRIBUTE);
		for (const name of Object.keys(variables)) {
			root.style.removeProperty(name);
		}
	};
}

/**
 * Top-level Privacy Suite provider. Mount it ONCE near the suite
 * root (e.g. inside `ZcashApp`) — nesting is harmless but adds no
 * value because the inner provider would shadow the outer one.
 */
export function PrivacyThemeProvider({
	initialThemeId,
	children,
}: PrivacyThemeProviderProps) {
	const [themeId, setThemeIdState] = useState<PrivacyThemeId>(
		() => resolvePrivacyThemeId(initialThemeId ?? _runtimeThemeId),
	);

	// Keep the module-level cache aligned with what this provider
	// instance is rendering so a second provider mounted later in
	// the same session inherits the user's last pick.
	useEffect(() => {
		_runtimeThemeId = themeId;
	}, [themeId]);

	// Whenever the theme changes (initial mount or user toggle),
	// stamp the document and write the variables. Cleanup removes
	// only the variables we set, leaving any unrelated inline
	// styles on `<html>` untouched.
	useEffect(() => {
		const cleanup = applyThemeToDocument(themeId);
		return cleanup;
	}, [themeId]);

	const setTheme = useCallback((id: PrivacyThemeId) => {
		const resolved = resolvePrivacyThemeId(id);
		_runtimeThemeId = resolved;
		setThemeIdState(resolved);
	}, []);

	const value = useMemo<PrivacyThemeContextValue>(() => ({
		theme: PRIVACY_THEME_REGISTRY[themeId],
		themeId,
		setTheme,
	}), [themeId, setTheme]);

	return (
		<PrivacyThemeContext.Provider value={value}>
			{children}
		</PrivacyThemeContext.Provider>
	);
}

PrivacyThemeProvider.displayName = 'PrivacyThemeProvider';
